import * as THREE from 'three';
import { physicsWorld, getRapier } from './physics.js';
import { rigidBodies } from './objects-shared.js';

let dynamicPages = [];

export const dynamicPageConfigs = [
    {
        name: 'Dynamic Page 1',
        htmlPath: './dynamicpage.html',
        position: { x: -60, y: 40, z: -150 },
        rotation: { x: 0, y: -Math.PI / 6, z: 0 },
        scale: 1,
        thickness: 8,
        canvasHeight: 1000
    }
];

async function loadDynamicHTML(htmlPath, canvasHeight = 2896) {
    try {
        const response = await fetch(htmlPath);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const htmlText = await response.text();
        
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.left = '-9999px';
        iframe.style.top = '-9999px';
        iframe.style.width = '2048px';
        iframe.style.height = canvasHeight + 'px';
        iframe.style.border = 'none';
        iframe.style.visibility = 'hidden';
        document.body.appendChild(iframe);
        
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(htmlText);
        iframeDoc.close();
        
        await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                reject(new Error('Iframe load timeout'));
            }, 5000);
            
            if (iframeDoc.readyState === 'complete') {
                clearTimeout(timeout);
                resolve();
            } else {
                iframe.onload = () => {
                    clearTimeout(timeout);
                    resolve();
                };
                iframe.onerror = () => {
                    clearTimeout(timeout);
                    reject(new Error('Iframe load error'));
                };
            }
        });
        
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Czekaj na załadowanie wszystkich obrazków
        const images = iframeDoc.querySelectorAll('img');
        if (images.length > 0) {
            await Promise.all(
                Array.from(images).map(img => {
                    if (img.complete) return Promise.resolve();
                    return new Promise((resolve, reject) => {
                        const timeout = setTimeout(() => {
                            console.warn(`Image load timeout: ${img.src}`);
                            resolve(); // Kontynuuj mimo timeout
                        }, 3000);
                        img.onload = () => {
                            clearTimeout(timeout);
                            resolve();
                        };
                        img.onerror = () => {
                            clearTimeout(timeout);
                            console.warn(`Image load error: ${img.src}`);
                            resolve(); // Kontynuuj mimo błędu
                        };
                    });
                })
            );
            await new Promise(resolve => setTimeout(resolve, 200)); // Dodatkowy czas na render
        }
        
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        canvas.width = 2048;
        canvas.height = canvasHeight;
        
        try {
            const serializer = new XMLSerializer();
            const htmlString = serializer.serializeToString(iframeDoc.documentElement);
            
            const svgData = `<svg xmlns="http://www.w3.org/2000/svg" width="2048" height="${canvas.height}">
                <foreignObject width="100%" height="100%">
                    <div xmlns="http://www.w3.org/1999/xhtml" style="width: 2048px;">
                        ${htmlString}
                    </div>
                </foreignObject>
            </svg>`;
            
            const img = new Image();
            const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(svgBlob);
            
            await new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                    reject(new Error('Image render timeout'));
                }, 3000);
                
                img.onload = () => {
                    clearTimeout(timeout);
                    resolve();
                };
                img.onerror = (e) => {
                    clearTimeout(timeout);
                    console.warn('SVG image render failed, using fallback method');
                    reject(e);
                };
                img.src = url;
            });
            
            ctx.fillStyle = '#ffffff00';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, 2048, canvas.height);
            URL.revokeObjectURL(url);
            
        } catch (renderError) {
            console.warn('SVG render failed, using canvas fallback:', renderError);
            
            ctx.fillStyle = '#000000';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            
            ctx.fillStyle = '#ffffff';
            ctx.font = 'bold 72px Arial';
            ctx.textAlign = 'center';
            ctx.fillText('Dynamic Page Loaded (Fallback)', canvas.width / 2, canvas.height / 2);
        }
        
        document.body.removeChild(iframe);
        
        console.log(`Successfully loaded dynamic page: ${htmlPath}`);
        return canvas;
        
    } catch (error) {
        console.error('Failed to load dynamic HTML:', error);
        
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = 2048;
        canvas.height = canvasHeight;
        
        ctx.fillStyle = '#1a1a1a';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        ctx.fillStyle = '#ff4444';
        ctx.font = 'bold 72px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('Failed to load dynamic page', canvas.width / 2, canvas.height / 2 - 100);
        ctx.fillStyle = '#ffffff';
        ctx.font = '36px Arial';
        ctx.fillText(`Path: ${htmlPath}`, canvas.width / 2, canvas.height / 2);
        ctx.font = '24px Arial';
        ctx.fillStyle = '#aaaaaa';
        ctx.fillText(`Error: ${error.message}`, canvas.width / 2, canvas.height / 2 + 60);
        
        return canvas;
    }
}

export async function createDPage(scene, htmlPath, position = { x: 0, y: 30, z: -100 }, rotation = { x: 0, y: 0, z: 0 }, scale = 1, thickness = 10, canvasHeight = 2896) {
    const RAPIER = getRapier();
    
    const htmlCanvas = await loadDynamicHTML(htmlPath, canvasHeight);
    
    const texture = new THREE.CanvasTexture(htmlCanvas);
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.anisotropy = 16;
    
    const pageWidth = 30 * scale;
    const pageHeight = (htmlCanvas.height / htmlCanvas.width) * pageWidth;
    
    const geometry = new THREE.PlaneGeometry(pageWidth, pageHeight);
    
    const material = new THREE.MeshStandardMaterial({
        map: texture,
        side: THREE.DoubleSide,
        metalness: 0.1,
        roughness: 0.3,
        emissive: 0x000000,
        emissiveIntensity: 0.0,
        transparent: true
    });
    
    const pageMesh = new THREE.Mesh(geometry, material);
    pageMesh.position.set(position.x, position.y, position.z);
    pageMesh.rotation.set(rotation.x, rotation.y, rotation.z);
    pageMesh.castShadow = true;
    pageMesh.receiveShadow = true;
    
    scene.add(pageMesh);
    
    const quaternion = new THREE.Quaternion();
    quaternion.setFromEuler(pageMesh.rotation);
    
    const bodyDesc = RAPIER.RigidBodyDesc.dynamic()
        .setTranslation(position.x, position.y, position.z)
        .setRotation({ x: quaternion.x, y: quaternion.y, z: quaternion.z, w: quaternion.w })
        .setLinearDamping(0.5)
        .setAngularDamping(0.8)
        .setCanSleep(false);
    
    const body = physicsWorld.createRigidBody(bodyDesc);
    
    const colliderDesc = RAPIER.ColliderDesc.cuboid(
        pageWidth / 2,
        pageHeight / 2,
        thickness / 2
    )
        .setRestitution(0.3)
        .setFriction(0.6)
        .setDensity(0.5);
    
    physicsWorld.createCollider(colliderDesc, body);
    
    pageMesh.userData.physicsBody = body;
    pageMesh.userData.isHTMLPage = true;
    pageMesh.userData.isDynamicPage = true;
    pageMesh.userData.texture = texture;
    pageMesh.userData.canvas = htmlCanvas;
    pageMesh.userData.htmlPath = htmlPath;
    
    rigidBodies.push(pageMesh);
    dynamicPages.push(pageMesh);
    
    return pageMesh;
}

export function getDynamicPages() {
    return dynamicPages;
}

export async function loadAllDynamicPages(scene) {
    for (const config of dynamicPageConfigs) {
        await createDPage(
            scene,
            config.htmlPath,
            config.position,
            config.rotation,
            config.scale,
            config.thickness,
            config.canvasHeight
        );
    }
}